/**
 * Passos e textos de instalação do app (PWA) por plataforma.
 *
 * Dados puros, sem JSX: os componentes resolvem o ícone pelo nome do
 * componente Feather. Os textos seguem a spec ao pé da letra (GUIA-01,
 * INST-02, INST-05 e INAPP-02). `⋯` é U+22EF e `⋮` é U+22EE, não reticências.
 */

import type { InstallPlatform } from "./platform";

export type InstallStepIcon =
  | "FiMoreHorizontal"
  | "FiShare"
  | "FiCheck"
  | "FiMoreVertical"
  | "FiPlusSquare"
  | "FiDownload";

export type InstallStep = {
  icon: InstallStepIcon;
  text: string;
};

export type InstallStepsContent = {
  /** Orientação de abrir no navegador, só no navegador interno (INAPP-02). */
  title?: string;
  steps: InstallStep[];
  /** Mostrar o botão "Copiar link" (INAPP-02). */
  copyLink: boolean;
  /** Candidato ao botão "Abrir no Chrome" (INAPP-03, atrás de constante na folha). */
  openInChrome: boolean;
};

const IOS_STEPS: InstallStep[] = [
  {
    icon: "FiMoreHorizontal",
    text: "Toque em ⋯ e em Compartilhar (ou direto no botão Compartilhar)",
  },
  {
    icon: "FiShare",
    text: "Na folha que abrir, toque em Mais (ou role a lista) e escolha Adicionar à Tela de Início",
  },
  { icon: "FiCheck", text: "Deixe Abrir como app web ligado e toque em Adicionar" },
];

const ANDROID_STEPS: InstallStep[] = [
  { icon: "FiMoreVertical", text: "Abra o menu ⋮ do navegador" },
  { icon: "FiPlusSquare", text: "Toque em Instalar app ou Adicionar à tela inicial" },
];

const DESKTOP_INSTRUCTION =
  "Use o ícone de instalar na barra de endereço ou o menu do navegador";

const DESKTOP_STEPS: InstallStep[] = [{ icon: "FiDownload", text: DESKTOP_INSTRUCTION }];

const IN_APP_STEP: InstallStep = {
  icon: "FiMoreHorizontal",
  text: "Toque em ⋯ e escolha Abrir no navegador",
};

export function getInstallSteps(
  platform: InstallPlatform,
  inApp: boolean
): InstallStepsContent {
  // A spec só define a orientação de navegador interno para iOS e Android. No
  // computador o navegador interno é improvável, e os passos de computador valem.
  if (inApp && platform === "ios") {
    return {
      title: "Para instalar, abra esta página no Safari",
      steps: [IN_APP_STEP],
      copyLink: true,
      openInChrome: false,
    };
  }

  if (inApp && platform === "android") {
    return {
      title: "Para instalar, abra esta página no Chrome",
      steps: [IN_APP_STEP],
      copyLink: true,
      openInChrome: true,
    };
  }

  const steps =
    platform === "ios"
      ? IOS_STEPS
      : platform === "android"
        ? ANDROID_STEPS
        : DESKTOP_STEPS;

  return { steps, copyLink: false, openInChrome: false };
}

/** A linha curta do cartão automático (INST-02, INST-05 e a de computador). */
export function getShortInstruction(platform: InstallPlatform): string {
  if (platform === "ios") {
    return "Toque em ⋯ e em Compartilhar, depois em Mais e em Adicionar à Tela de Início";
  }

  if (platform === "android") {
    return "Abra o menu ⋮ e toque em Instalar app ou Adicionar à tela inicial";
  }

  return DESKTOP_INSTRUCTION;
}
